import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Linkify from 'react-linkify';
import * as api from 'constants/api';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography } from '@material-ui/core';
import PATHS from 'constants/paths';
import FileMessage from './FileMessage';

const types = Object.values(api).filter((con) => con.indexOf('MESSAGE_TYPE_') === 0);
const newlineRegex = /(\r\n|\r|\n)/g;

const translateText = (text, intl) => {
  return text
    .replace(
      /\[Filtered email\]/gi,
      intl.formatMessage({ id: '[Filtered email]', defaultMessage: '[Filtered email]' }),
    )
    .replace(
      /\[Filtered link\]/gi,
      intl.formatMessage({ id: '[Filtered link]', defaultMessage: '[Filtered link]' }),
    )
    .replace(
      /\[Filtered phone\]/gi,
      intl.formatMessage({ id: '[Filtered phone]', defaultMessage: '[Filtered phone]' }),
    );
};

const toLinks = (text, key, intl) => {
  return (
    <Linkify key={key} properties={{ target: '_blank', rel: 'nofollow noopener' }}>
      {translateText(text, intl)}
    </Linkify>
  );
};

const formatText = (text, hideLink = false, intl) =>
  // eslint-disable-next-line react/no-array-index-key
  text.split(newlineRegex).map((line, i) => {
    if (line.match(newlineRegex)) {
      return <br key={i} />;
    }

    if (hideLink) {
      return line;
    }

    return toLinks(line, i, intl);
  });

const formatTextWithReviewLink = (msg, hideLink = false, intl) => {
  const text = formatText(msg.text, hideLink, intl);
  const url =
    msg.meta && msg.meta.cottage_id && msg.meta.booking_id
      ? PATHS.reviews.add
          .replace(':cottageId', msg.meta.cottage_id)
          .replace(':bookingId', msg.meta.booking_id)
      : PATHS.reviews.add.replace(':cottageId', msg.resource_id).replace('/:bookingId', '');

  if (hideLink) {
    return <>{text} ...</>;
  }

  return (
    <>
      {text}{' '}
      <Link to={url}>
        [<FormattedMessage id="Add review" defaultMessage="Add review" />]
      </Link>
    </>
  );
};

const seeOffer = (type) =>
  type === 'MESSAGE_TYPE_BOOKING_CREATED' ? (
    <FormattedMessage id="msg.seeOffer" defaultMessage="Click here to view the offer" />
  ) : (
    <FormattedMessage id="msg.seeBooking" defaultMessage="Click here to view the booking" />
  );

const you = <FormattedMessage id="You" defaultMessage="You" />;

const Warning = () => (
  <>
    <FormattedMessage
      id="conversations.warningPopoverText"
      defaultMessage="This accommodation is S-certified.
        For your security, we recommend that all communication
        should take place via stugknuten.com.
        Managing your payment via Stugknuten provides you with increased security
        and does not involve any extra costs for you as guest."
    />
    <br />
    <br />
    <FormattedMessage
      id="conversations.warningPopoverText2"
      defaultMessage="If you make payment in some way
        other than via stugknuten.com,
        we will not be able to assist in the event of any problems."
    />
  </>
);

const HostWarning = () => (
  <>
    <FormattedMessage
      id="conversations.warningPopoverText4"
      defaultMessage="Payment outside of Stugknuten is not allowed according to our terms of service"
    />
  </>
);

const renderMessage = ({ addYouPrefix, hideLink, isMy, msg, userName, intl }) => {
  const link = `/conversations/${msg.conversation_id}/bookings/${msg.resource_id}`;
  const name = isMy ? you : userName;
  const seeOfferLink = hideLink ? '' : <Link to={link}>[{seeOffer(msg.type)}]</Link>;

  if (msg.type === api.MESSAGE_TYPE_FILE && msg.deleted_at) {
    return <FormattedMessage id="Attachment deleted" defaultMessage="Attachment deleted" />;
  }

  switch (msg.type) {
    case api.MESSAGE_TYPE_HOST_COMMUNICATION_WARNING: {
      return <HostWarning />;
    }
    case api.MESSAGE_TYPE_COMMUNICATION_WARNING: {
      return <Warning />;
    }
    // Text
    case api.MESSAGE_TYPE_TEXT: {
      if (addYouPrefix) {
        return (
          <>
            {you}: {translateText(msg.text, intl)}
          </>
        );
      }
      return formatText(translateText(msg.text, intl), hideLink, intl);
    }
    case api.MESSAGE_TYPE_ASK_FOR_REVIEW: {
      return formatTextWithReviewLink(msg, hideLink, intl);
    }
    // File
    case api.MESSAGE_TYPE_FILE: {
      return <FileMessage msg={msg} name={name} hideLink={hideLink} />;
    }
    // Cancelled
    case api.MESSAGE_TYPE_BOOKING_CANCELLED: {
      return (
        <>
          {name}{' '}
          <FormattedMessage
            id="msg.cancelled"
            defaultMessage="cancelled booking #{id}"
            values={{
              id: msg.resource_id,
            }}
          />{' '}
          {seeOfferLink}
        </>
      );
    }
    // Created
    case api.MESSAGE_TYPE_BOOKING_CREATED: {
      return (
        <>
          {name}{' '}
          {isMy ? (
            <FormattedMessage
              id="msg.created"
              defaultMessage="sent a booking offer #{id}"
              values={{
                id: msg.resource_id,
              }}
            />
          ) : (
            <FormattedMessage
              id="msg.sentOffer"
              defaultMessage="sent you an booking offer #{id}"
              values={{
                id: msg.resource_id,
              }}
            />
          )}{' '}
          {seeOfferLink}
        </>
      );
    }
    // Paid
    case api.MESSAGE_TYPE_BOOKING_PAID: {
      return (
        <>
          {name}{' '}
          <FormattedMessage
            id="msg.paid"
            defaultMessage="accepted and paid your booking offer #{id}"
            values={{
              id: msg.resource_id,
            }}
          />{' '}
          {seeOfferLink}{' '}
        </>
      );
    }
    default: {
      return null;
    }
  }
};

const Message = ({ addYouPrefix, hideLink, isMy, isUnRead, msg, userName }) => {
  const intl = useIntl();

  return (
    <Typography variant={isUnRead ? 'subtitle2' : 'body2'}>
      {renderMessage({
        addYouPrefix,
        hideLink,
        isMy,
        msg,
        userName,
        intl,
      })}
    </Typography>
  );
};

Message.propTypes = {
  addYouPrefix: PropTypes.bool,
  hideLink: PropTypes.bool,
  isMy: PropTypes.bool.isRequired,
  isUnRead: PropTypes.bool,
  msg: PropTypes.shape({
    conversation_id: PropTypes.number.isRequired,
    resource_id: PropTypes.number,
    text: PropTypes.string,
    type: PropTypes.oneOf(types).isRequired,
  }).isRequired,
  userName: PropTypes.string.isRequired,
};

export default Message;
